import {
	MAIN_SITE_LABEL,
	mainSiteUrl,
	ORGANIZATION,
	PRODUCT,
} from "@nwga/contracts/organization";
import { useActor, useTRPC } from "@nwga/features/client";
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "@nwga/ui/components/avatar";
import { Button } from "@nwga/ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@nwga/ui/components/dropdown-menu";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@nwga/ui/components/sheet";
import { ThemeToggle } from "@nwga/ui/components/theme-toggle";
import { cn } from "@nwga/ui/lib/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ExternalLink, Menu } from "lucide-react";
import * as React from "react";
import { signOut } from "../lib/auth.ts";
import {
	HOME_PATH,
	type TopLevelSectionEntry,
	topLevelSectionsFor,
} from "../navigation.ts";
import { Container } from "./container.tsx";
import { Logo } from "./logo.tsx";

/**
 * The header.
 *
 * Two things it has to communicate at once: this is part of northwestgeorgia.us,
 * and you have not lost your way back. So the utility bar is exactly that — the
 * way back, and the page that explains who is behind this — and the logo points
 * at this site's own home page.
 *
 * It used to carry the Authority's phone number, postal address and a link to
 * site selection as well. None of them belonged: nobody phones a switchboard to
 * find a job, an address in a sticky bar is a permanent tenant of the screen
 * that answers a question almost nobody asks here, and "site selection" is a
 * corporate-relocation service with nothing to do with this platform. They are
 * all still in the footer, or on the main site, which is where they are looked
 * for.
 *
 * Which links exist is not decided here: `src/navigation.ts` is the registry,
 * and this renders whatever is in it.
 */

/** The one page on the main site worth reaching from the utility bar. */
const UTILITY = [
	{ href: mainSiteUrl("/about"), label: "About the Authority" },
] as const;

const linkBase =
	"rounded-sm px-3 py-2 text-sm font-medium tracking-tight whitespace-nowrap transition-colors";

/** Active-link styling copied from the main site: a gold underline, not a pill. */
const activeLink =
	"text-primary underline decoration-brand decoration-2 underline-offset-8";

export function AppHeader() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [menuOpen, setMenuOpen] = React.useState(false);

	const session = useQuery(trpc.session.queryOptions());
	const user = session.data?.user ?? null;
	// The client decides what to render from the *same* permission rules the
	// server enforces — see `@nwga/auth/permissions`, which is why that module
	// depends on nothing but contracts.
	const actor = useActor();

	const sections = topLevelSectionsFor(actor);

	const handleSignOut = async () => {
		await signOut();
		// Everything in the cache was fetched as the previous user. Clearing beats
		// invalidating: a stale private listing must not be readable for the
		// moment it takes a refetch to 404.
		queryClient.clear();
		await navigate({ to: HOME_PATH });
	};

	return (
		<header className="sticky top-0 z-50 w-full">
			<div className="hidden bg-band text-band-foreground md:block">
				{/*
          Both bars take their height from `--header-*-height` in `globals.css`
          rather than from a literal here. Anything that has to sit clear of a
          sticky header — the board's filter rail, an in-page anchor — offsets
          itself by the same tokens, so the two cannot disagree.
        */}
				<Container className="flex h-header-utility items-center justify-between text-xs">
					{/*
            The way back to the main site, stated as a destination rather than
            as one more link in a row. This site is a tab on northwestgeorgia.us
            and a visitor who arrived that way should never have to reach for
            the back button to return.
          */}
					<a
						href={mainSiteUrl(PRODUCT.mainSitePath)}
						className="flex items-center gap-1.5 font-medium text-band-foreground/85 transition-colors hover:text-band-foreground"
					>
						<ArrowLeft className="size-3.5" aria-hidden="true" />
						Back to {MAIN_SITE_LABEL}
					</a>

					<nav aria-label="Utility" className="flex items-center gap-4">
						{UTILITY.map((item) => (
							<a
								key={item.href}
								href={item.href}
								target="_blank"
								rel="noreferrer"
								className="flex items-center gap-1 font-medium text-band-foreground/85 transition-colors hover:text-band-foreground"
							>
								{item.label}
								<ExternalLink className="size-3" aria-hidden="true" />
								<span className="sr-only">(opens in new tab)</span>
							</a>
						))}
					</nav>
				</Container>
			</div>

			<div className="border-b border-border bg-background/95 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/85">
				<Container className="flex h-header-bar items-center justify-between gap-4 py-2">
					<Link to={HOME_PATH} aria-label={`${PRODUCT.name} — home`}>
						<Logo />
					</Link>

					<div className="flex items-center gap-2">
						<nav
							className="hidden items-center gap-0.5 lg:flex"
							aria-label="Main"
						>
							{sections.map((item) => (
								<Link
									key={item.to}
									to={item.to}
									className={cn(
										linkBase,
										"text-foreground/80 hover:bg-accent hover:text-accent-foreground",
									)}
									// Prefix matching, so the section stays lit on a job detail
									// page the same way the main site's nav does — except for the
									// sections that declare `exact`, of which Home is one: `/` is
									// a prefix of every path on the site.
									activeOptions={{ exact: item.exact }}
									activeProps={{ className: activeLink }}
								>
									{item.label}
								</Link>
							))}
						</nav>

						<ThemeToggle />

						{session.isPending ? (
							<div className="size-9 animate-pulse rounded-full bg-muted" />
						) : user === null ? (
							<>
								<Button
									asChild
									variant="ghost"
									size="lg"
									className="hidden sm:inline-flex"
								>
									<Link to="/sign-in">Sign in</Link>
								</Button>
								<Button asChild size="lg" className="hidden sm:inline-flex">
									<Link to="/sign-up">Sign up</Link>
								</Button>
							</>
						) : (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="ghost"
										size="icon-lg"
										aria-label="Account menu"
									>
										<Avatar className="size-8">
											{user.image !== null && (
												<AvatarImage src={user.image} alt="" />
											)}
											<AvatarFallback>
												{user.name.slice(0, 2).toUpperCase()}
											</AvatarFallback>
										</Avatar>
									</Button>
								</DropdownMenuTrigger>

								{/*
                  Your account, and nothing else. Dashboard and Admin used to
                  live here too, which meant the answer to "where do I go" was
                  split between a nav bar and a menu behind an avatar. They are
                  sections of the site, so they are in the section nav — on
                  narrow screens, in the sheet beside it.
                */}
								<DropdownMenuContent align="end" className="w-56">
									<div className="px-2 py-1.5">
										<p className="truncate font-medium text-sm">{user.name}</p>
										<p className="truncate text-muted-foreground text-xs">
											{user.email}
										</p>
									</div>
									<DropdownMenuSeparator />
									<DropdownMenuItem asChild>
										<Link to="/settings/account">Account settings</Link>
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem onSelect={() => void handleSignOut()}>
										Sign out
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						)}

						<MobileNav
							open={menuOpen}
							onOpenChange={setMenuOpen}
							signedIn={user !== null}
							sections={sections}
						/>
					</div>
				</Container>
			</div>
		</header>
	);
}

function MobileNav({
	open,
	onOpenChange,
	signedIn,
	sections,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	signedIn: boolean;
	sections: readonly TopLevelSectionEntry[];
}) {
	const close = () => onOpenChange(false);

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetTrigger asChild>
				<Button
					variant="outline"
					size="icon-lg"
					className="lg:hidden"
					aria-label="Open menu"
				>
					<Menu className="size-5" />
				</Button>
			</SheetTrigger>

			<SheetContent
				side="right"
				className="w-[88vw] max-w-sm gap-0 overflow-y-auto p-0"
			>
				<SheetHeader className="border-b p-5">
					<SheetTitle className="text-base">{PRODUCT.name}</SheetTitle>
				</SheetHeader>

				<nav aria-label="Mobile" className="p-2 text-sm">
					{sections.map((item) => (
						<Link
							key={item.to}
							to={item.to}
							onClick={close}
							className="flex items-start gap-3 rounded-md px-3 py-3 hover:bg-accent"
							activeOptions={{ exact: item.exact }}
							activeProps={{ className: "text-primary" }}
						>
							<item.icon
								className="mt-0.5 size-4 shrink-0 text-brand"
								aria-hidden="true"
							/>
							<span className="min-w-0">
								<span className="block font-medium text-foreground/90">
									{item.label}
								</span>
								<span className="mt-0.5 block text-muted-foreground text-xs">
									{item.description}
								</span>
							</span>
						</Link>
					))}

					{!signedIn && (
						<div className="flex flex-col gap-2 p-3 sm:hidden">
							<Button asChild size="lg">
								<Link to="/sign-up" onClick={close}>
									Sign up
								</Link>
							</Button>
							<Button asChild size="lg" variant="outline">
								<Link to="/sign-in" onClick={close}>
									Sign in
								</Link>
							</Button>
						</div>
					)}

					<div className="mt-2 border-t p-3">
						<p className="px-1 pb-2 font-semibold text-muted-foreground text-xs uppercase tracking-[0.14em]">
							{ORGANIZATION.abbr}
						</p>

						<a
							href={mainSiteUrl(PRODUCT.mainSitePath)}
							className="flex items-center gap-2 rounded-md px-3 py-2.5 font-medium text-foreground/90 text-sm hover:bg-accent"
						>
							<ArrowLeft className="size-3.5 text-brand" aria-hidden="true" />
							Back to {MAIN_SITE_LABEL}
						</a>

						{UTILITY.map((item) => (
							<a
								key={item.href}
								href={item.href}
								target="_blank"
								rel="noreferrer"
								className="flex items-center gap-1.5 rounded-md px-3 py-2.5 text-foreground/80 text-sm hover:bg-accent"
							>
								{item.label}
								<ExternalLink
									className="size-3.5 text-brand"
									aria-hidden="true"
								/>
								<span className="sr-only">(opens in new tab)</span>
							</a>
						))}
					</div>
				</nav>
			</SheetContent>
		</Sheet>
	);
}
